import clsx from "clsx";
import { useState, useMemo, useEffect } from "react";
import { matchSorter } from "match-sorter";

import { ModalContainer } from "components/modal/modal.container";
import { convertToTitleCase } from "utils/string";
import { SearchFilterComponent } from "components/table/search-filter.component";
import { useGetAllCitiesList } from "hooks/member";

export function ModalSelectCity({ visible, initialValue, onClose, onChange, multiple = false }) {
	const [searchKeyword, setSearchKeyword] = useState("");
	const { data: allCities } = useGetAllCitiesList();
	const [selected, setSelected] = useState(initialValue || (multiple ? [] : null));

	useEffect(() => {
		if (initialValue && visible) {
			setSelected(initialValue);
		} else {
			setSelected(multiple ? [] : null);
		}
	}, [initialValue, multiple, visible]);

	const listCities = useMemo(() => {
		if (!allCities?.data) return [];
		const filtered = matchSorter(allCities.data, searchKeyword, { keys: ["name"] });


		return filtered.sort((a, b) => {
			let aSelected = false;
			let bSelected = false;
			// Use initialValue (or default) to determine sort order, avoiding live jumps
			const sortBasis = initialValue || (multiple ? [] : null);

			if (multiple) {
				if (Array.isArray(sortBasis)) {
					aSelected = sortBasis.some(i => i.id === a.id);
					bSelected = sortBasis.some(i => i.id === b.id);
				}
			} else {
				aSelected = sortBasis?.id === a.id;
				bSelected = sortBasis?.id === b.id;
			}

			if (aSelected === bSelected) return 0;
			return aSelected ? -1 : 1;
		});
	}, [allCities, searchKeyword, initialValue, multiple]);

	const handleSelect = (item) => {
		if (multiple) {
			setSelected(prev => {
				const prevArray = Array.isArray(prev) ? prev : [];
				const exists = prevArray.find(i => i.id === item.id);
				if (exists) {
					return prevArray.filter(i => i.id !== item.id);
				}
				return [...prevArray, { name: item.name, id: item.id }];
			});
		} else {
			setSelected({
				name: item.name,
				id: item.id,
			});
		}
	};

	return (
		<ModalContainer visible={visible}>
			<div id="show-item" className={clsx("rounded-2xl bg-white shadow-md p-4 w-[400px]", !visible && "hidden")}>
				<div className="relative text-center py-4">
					<div className="font-bold text-red-50">City</div>

					<button type="button" className="absolute top-0 right-0 border-0" onClick={onClose}>
						<span className="icon-ico-close"></span>
					</button>
				</div>

				<SearchFilterComponent
					placeholder="Search city.."
					containerClassName="relative w-[100%] mr-2"
					onSearch={({ keyword }) => setSearchKeyword(keyword)}
				/>

				<div className="content-scroll">
					<div className="my-6">
						{listCities?.map(itemLevel => (
							<div className="rounded-lg border border-solid border-grey-10 mb-2" key={itemLevel.id}>
								<div className="form-check flex items-center my-3 mx-4">
									<input
										className="form-check-input appearance-none h-4 w-4 border border-gray-300 rounded-sm bg-white checked:bg-red-50 checked:border-red-50 focus:outline-none transition duration-200 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
										type="checkbox"
										id={itemLevel.id}
										checked={
											multiple
												? Array.isArray(selected) && selected.some(i => i.id === itemLevel.id)
												: selected?.id === itemLevel.id
										}
										onChange={() => handleSelect(itemLevel)}
									/>
									<label
										className="form-check-label w-[90%] block text-grey-60 font-semibold text-sm pl-2 cursor-pointer"
										htmlFor={itemLevel.id}>
										{itemLevel.name && convertToTitleCase(itemLevel.name)}
									</label>
								</div>
							</div>
						))}
					</div>
				</div>

				<div className="my-6 flex justify-center gap-4">
					<div className="w-2/4">
						<button
							className="block w-full rounded-xl border border-red-50 text-red-50 py-2 px-8 hover:bg-red-50 hover:text-white transition-all"
							type="button"
							onClick={() => setSelected(multiple ? [] : null)}>
							<span>Clear</span>
						</button>
					</div>
					<div className="w-2/4">
						<button
							className="block w-full rounded-xl bg-red-50 text-white py-2 px-8 hover:bg-red-70 transition-all"
							type="button"
							onClick={() => {
								onClose();
								setSearchKeyword("");
								onChange({ selected });
							}}>
							<span className="icon-ico-user-register"></span>
							<span>Apply</span>
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
}
